import React, { useEffect, useMemo, useState } from 'react';
import {
  X,
  User,
  Mail,
  Phone,
  Calendar,
  Globe,
  GraduationCap,
  DollarSign,
  Save,
  Plus,
  CheckCircle,
  Clock,
  Send,
  FileText,
  Target,
  MessageSquare,
  Users,
  Building2,
  MapPin,
  Heart,
  Baby,
  Briefcase,
  BookOpen,
  Star,
  Tag,
  AlertTriangle,
  Info
} from 'lucide-react';
import { useAuth, mockUsers } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface AddLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (leadData: any) => Promise<{ ok: boolean; error?: string }>;
  defaultEmail?: string;
  prefilledData?: any;
  prefilledPhone?: string;
  token?: string;
  onSaved?: (createdLead: any) => void;
  initialRegistrationYear?: string;
}

type ContactRole = 'student' | 'guardian';
type MeetingType = 'phone' | 'video' | 'in-person' | 'whatsapp' | 'email';
type SourceType = 'website' | 'referral' | 'social-media' | 'advertisement' | 'walk-in' | 'other';
type PriorityType = 'low' | 'medium' | 'high';
type StatusType =
  | 'lead'
  | 'contacted'
  | 'no_answer'
  | 'busy'
  | 'meeting_scheduled'
  | 'proposal_sent'
  | 'accepted'
  | 'rejected'
  | 'enrolled';

const OFFICES = [
  {
    id: 'skyland',
    name: 'Skyland Ofis',
    address: 'Huzur, Azerbaycan Cd. No:4 B Blok Kat: 8 Ofis No: 125-126-127 Skyland, 34415 Sarıyer/İstanbul',
    mapLink: 'https://maps.app.goo.gl/Hoz9snT9KXTc815A8'
  },
  {
    id: 'kadikoy',
    name: 'Kadıköy Ofis',
    address: 'Osmanağa Mah. General Asım Gündüz Cad, Bahariye, Nevzemin Sk. No:2/20 Kat:5, 34714 Kadıköy/İstanbul',
    mapLink: 'https://maps.app.goo.gl/cjoN5VmTBTr69Bv38'
  },
  {
    id: 'bursa',
    name: 'Bursa Ofis',
    address: 'İhsaniye Mh. FSM Bulvarı Aydemir - Rızvanoğlu İş Merkezi No.12/C A Blok Kat: 10 D:63, 16130 Ni̇lüfer/Bursa',
    mapLink: 'https://maps.app.goo.gl/g5AuxCHNge4uHCri9'
  },
  {
    id: 'ankara',
    name: 'Ankara Ofis',
    address: 'Kızılırmak Mh.1443 Cd No:9 YDA Center A/3 Blok Kat:6 Ofis Numarası:177, 06510 Çankaya/Ankara',
    mapLink: 'https://maps.app.goo.gl/GZR9YemLPcFC7Wiy9'
  },
  {
    id: 'fethiye',
    name: 'Fethiye Ofis',
    address: 'Akarca, Mustafa Kemal Blv. No:173 B (Hipokampüs, 48300 Fethiye/Muğla',
    mapLink: 'https://maps.app.goo.gl/zvxoDHRqPpzwStj89'
  },
  {
    id: 'izmir',
    name: 'İzmir Ofis',
    address: '',
    mapLink: ''
  }
];




// --- Tıp (İngilizce) için üniversite listeleri ---
const MEDICAL_UNIVERSITIES = [
  { name: 'Bari Üniversitesi', type: 'Devlet' },
  { name: 'Bologna Üniversitesi', type: 'Devlet' },
  { name: 'Napoli Campania "Luigi Vanvitelli" Üniversitesi', type: 'Devlet' },
  { name: 'Milano Üniversitesi', type: 'Devlet' },
  { name: 'Milano-Bicocca Üniversitesi', type: 'Devlet' },
  { name: 'Napoli Federico II Üniversitesi', type: 'Devlet' },
  { name: 'Pavia Üniversitesi', type: 'Devlet' },
  { name: 'Padova Üniversitesi', type: 'Devlet' },
  { name: 'Roma La Sapienza Üniversitesi', type: 'Devlet' },
  { name: 'Roma Tor Vergata Üniversitesi', type: 'Devlet' },
  { name: 'Torino Üniversitesi', type: 'Devlet' },
  { name: 'Messina Üniversitesi', type: 'Devlet' },
  { name: 'Parma Üniversitesi', type: 'Devlet' },
  { name: 'Politecnica di Marche Üniversitesi', type: 'Devlet' },
  { name: 'Catania Üniversitesi', type: 'Devlet' },
  { name: 'Cagliari Üniversitesi', type: 'Devlet' },
  { name: 'Siena Üniversitesi (Diş Hekimliği)', type: 'Devlet' },
  { name: 'Roma La Sapienza Üniversitesi (Diş Hekimliği)', type: 'Devlet' },
  { name: 'Bologna Üniversitesi (Veterinerlik)', type: 'Devlet' },
  { name: 'Humanitas Üniversitesi', type: 'Özel' },
  { name: 'Cattolica Üniversitesi', type: 'Özel' },
  { name: 'Milano San Raffaele Üniversitesi', type: 'Özel' },
  { name: 'Roma Unicamillus Üniversitesi', type: 'Özel' },
  { name: 'Campus Bio Medico Üniversitesi', type: 'Özel' }
];

const AddLeadModal: React.FC<AddLeadModalProps> = ({
  isOpen,
  onClose,
  onSave,
  defaultEmail,
  prefilledData,
  prefilledPhone,
  token,
  onSaved,
  initialRegistrationYear
}) => {
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [profiles, setProfiles] = useState<any[]>([]);

  const [isHistoricalLead, setIsHistoricalLead] = useState(false);
  const [sendReminder, setSendReminder] = useState(false);


  const [formData, setFormData] = useState({
    // Temel bilgiler
    first_name: '',
    last_name: '',
    emails: [''] as string[],
    phone: '',
    nationality: 'Türkiye',
    has_italy_residence_permit: false,
    has_other_citizenship: false,
    other_citizenship_country: '',
    created_by: '' as string,

    // İletişim bilgileri
    contact_role: 'student' as ContactRole,
    student_info: {
      first_name: '',
      last_name: '',
      emails: [''] as string[],
      phone: '',
      nationality: 'Türkiye',
      education_level: '',
      english_level: '',
      italian_level: ''
    },
    guardian_info: {},

    // Eğitim bilgileri
    interested_services: [] as string[],
    service_year: '2025',
    academic_year: '2025-2026',
    registration_year: initialRegistrationYear || '',

    // YENİ: Tıp akışı (İngilizce)
    medical_track: 'none' as 'none' | 'tip_ingilizce' | 'both',

    interested_programs: [] as string[],
    interested_universities: [] as string[],
    interested_countries: ['İtalya'] as string[],
    education_level: '',
    english_level: '',
    italian_level: '',
    work_experience: '',

    // Görüşme bilgileri
    meeting_date: '',
    meeting_time: '',
    meeting_consultant: '',
    meeting_type: 'phone' as MeetingType,
    meeting_summary: '',

    // Durum bilgileri
    source: 'website' as SourceType,
    status: 'lead' as StatusType,
    priority: 'medium' as PriorityType,
    is_serious_candidate: false,
    visa_info_provided: false,
    has_discount: false,
    discount_info: '',
    discussed_price: '',
    price_notes: '',
    additional_payment: '',
    contract_sent: false,
    form_completed: false,
    email_sent: false,
    is_registered: false,
    tracking_status: 'new_lead' as 'new_lead' | 'enrolled' | 'rejected',
    reference_source: '',

    // Follow-up
    follow_up_required: false,
    follow_up_date: '',            // YYYY-MM-DD
    follow_up_notes: '',
    follow_up_name: '',
    follow_up_surname: '',
    follow_up_phone: '',
    follow_up_status: 'pending' as 'pending' | 'scheduled' | 'completed' | 'no-response',
    follow_up_address: '',
    follow_up_time: '',
    notes: ''
  });




  const [newProgram, setNewProgram] = useState('');
  const [newUniversity, setNewUniversity] = useState('');
  const [filteredUniversities, setFilteredUniversities] = useState<string[]>([]);

  // Program / Üniversite listeleri (genel)
  const [availablePrograms] = useState<string[]>([
    'İşletme Yönetimi', 'Uluslararası İşletme', 'İnsan Kaynakları Yönetimi', 'Pazarlama', 'Finans', 'Muhasebe',
    'Ekonomi', 'Ekonometri', 'Uluslararası Ekonomi', 'Kamu Yönetimi', 'Siyaset Bilimi', 'Uluslararası İlişkiler',
    'Bilgisayar Mühendisliği', 'Elektrik-Elektronik Mühendisliği', 'Makine Mühendisliği', 'İnşaat Mühendisliği',
    'Endüstri Mühendisliği', 'Yazılım Mühendisliği', 'Çevre Mühendisliği', 'Gıda Mühendisliği', 'Kimya Mühendisliği',
    'Tıp', 'Diş Hekimliği', 'Eczacılık', 'Hemşirelik', 'Fizyoterapi', 'Beslenme ve Diyetetik', 'Veteriner Hekim', 'Psikoloji',
    'Hukuk', 'Sosyoloji', 'Felsefe', 'Tarih', 'Coğrafya', 'Arkeoloji', 'Sanat Tarihi', 'Antropoloji',
    'Mimarlık', 'İç Mimarlık', 'Endüstriyel Tasarım', 'Grafik Tasarım', 'Moda Tasarımı', 'Güzel Sanatlar', 'Müzik',
    'Matematik', 'Fizik', 'Kimya', 'Biyoloji', 'İstatistik', 'Astronomi',
    'İletişim', 'Gazetecilik', 'Halkla İlişkiler', 'Radyo TV Sinema', 'Dijital Medya',
    'Eğitim Bilimleri', 'Okul Öncesi Öğretmenliği', 'Sınıf Öğretmenliği', 'İngilizce Öğretmenliği',
    'Spor Bilimleri', 'Beden Eğitimi Öğretmenliği', 'Antrenörlük', 'Rekreasyon',
    'Turizm İşletmeciliği', 'Otel İşletmeciliği', 'Gastronomi', 'Aşçıbaşılık'
  ]);

  const [availableUniversities] = useState<string[]>([
    'Accademia Italiana',
    'Accademia Italiana Firenze',
    'Aosta Vadisi Üniversitesi',
    'Bari Aldo Moro Üniversitesi',
    'Basilicata Üniversitesi',
    'Bergamo Üniversitesi',
    'Bocconi Üniversitesi',
    'Bologna Üniversitesi',
    'Brescia Üniversitesi',
    'Brera Güzel Sanatlar Akademisi',
    'Cagliari Üniversitesi',
    'Calabria Üniversitesi',
    'Camerino Üniversitesi',
    "Ca' Foscari Venezia Üniversitesi",
    'Campus Bio Medico Üniversitesi',
    'Carlo Bo Urbino Üniversitesi',
    'Carlo Cattaneo Üniversitesi',
    'Cassino Üniversitesi',
    'Catania Üniversitesi',
    'Catanzaro Magna Graecia Üniversitesi',
    'Cattolica Üniversitesi',
    'Doğu Piyemonte Üniversitesi',
    'Domus Academy',
    'Enna Kore Üniversitesi',
    'Ferrara Üniversitesi',
    'FIDI – Florence Institute of Design International',
    'Floransa Üniversitesi',
    'Foggia Üniversitesi',
    'Free Bozen-Bolzano Üniversitesi',
    "G. d'Annunzio Chieti-Pescara Üniversitesi",
    'Genova Üniversitesi',
    'Guglielmo Marconi Üniversitesi',
    'Humanitas Üniversitesi',
    'IED – Istituto Europeo di Design',
    'IMT Lucca İleri Araştırmalar Okulu',
    'Insubria Üniversitesi',
    'Istituto Marangoni',
    'IULM Dil ve İletişim Üniversitesi',
    'John Cabot Üniversitesi',
    'Johns Hopkins Üniversitesi',
    "L'Aquila Üniversitesi",
    'Link Campus Üniversitesi',
    'Luiss Guido Carli Üniversitesi',
    'Lumsa Üniversitesi',
    'Macerata Üniversitesi',
    'Messina Üniversitesi',
    'Milano Bicocca Üniversitesi',
    'Milano G. Verdi Müzik Konservatuarı',
    'Milano Üniversitesi',
    'Modena Üniversitesi',
    'Molise Üniversitesi',
    'Naba Üniversitesi',
    'Napoli Federico II Üniversitesi',
    'Napoli Luigi Vanvitelli Üniversitesi',
    'Napoli Orientale Üniversitesi',
    'Napoli Parthenope Üniversitesi',
    'Padova Üniversitesi',
    'Palermo Üniversitesi',
    'Papalık Gregoryen Üniversitesi',
    'Papalık Lateranense Üniversitesi',
    'Parma Üniversitesi',
    'Pavia İleri Araştırmalar Enstitüsü Üniversitesi',
    'Pavia Üniversitesi',
    'Perugia Üniversitesi',
    'Pisa Üniversitesi',
    'Politecnico di Bari Üniversitesi',
    'Politecnico di Milano Üniversitesi',
    'Politecnico di Torino Üniversitesi',
    'Politecnica di Marche Üniversitesi',
    'Reggio Calabria Akdeniz Üniversitesi',
    'Roma Avrupa Üniversitesi',
    'Roma La Sapienza Üniversitesi',
    'Roma Tre Üniversitesi',
    'Roma Tor Vergata Üniversitesi',
    'Roma Unicamillus Üniversitesi',
    'Rome Business School',
    'RUFA – Rome University of Fine Arts',
    'Saint Camillus Üniversitesi',
    'Salento Üniversitesi',
    'Salerno Üniversitesi',
    'Sannio Üniversitesi',
    'Sassari Üniversitesi',
    'Scuola Normale Superiore di Pisa',
    'Scuola Politecnica di Design',
    'Siena Üniversitesi',
    'Siena ve Perugia Yabancılar Üniversitesi',
    'Teramo Üniversitesi',
    'Torino Üniversitesi',
    'Trento Üniversitesi',
    'Trieste Üniversitesi',
    'Tuscia Üniversitesi',
    'Udine Üniversitesi',
    'Uluslararası İleri Araştırmalar Enstitüsü',
    'Uluslararası Venedik Üniversitesi',
    'Venedik IUAV Üniversitesi',
    'Verona Üniversitesi',
    'Vita Salute San Raffaele Üniversitesi'
  ]);

  // Akademik yıl: 2024-2025'ten 2036-2037'ye kadar
  const academicYears = useMemo(() => {
    const years: string[] = [];
    for (let start = 2024; start <= 2036; start++) {
      years.push(`${start}-${start + 1}`);
    }
    return years;
  }, []);

  // Profiles'ı çek
  useEffect(() => {
    const fetchProfiles = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id, name, role, unit_type, branch_id')
        .eq('is_active', true);
      if (data) {
        setProfiles(data);
      }
    };
    fetchProfiles();
  }, []);

  // Açıldığında otomatik doldurma
  useEffect(() => {
    if (isOpen) {
      // Modal açıldığında historical lead checkbox'ını sıfırla
      setIsHistoricalLead(false);

      const now = new Date();
      const currentDate = now.toISOString().split('T')[0];
      const currentTime = now.toTimeString().slice(0, 5);

      setFormData(prev => {
        // Eğer kullanıcı sales unit_type'ındaysa danışman olarak seçilsin, değilse boş bıraksın
        const currentUserProfile = profiles.find(p => p.id === (user as any)?.id);
        const defaultConsultantId = currentUserProfile?.unit_type === 'sales' ? (user as any)?.id || '' : '';

        const baseData = {
          ...prev,
          // İlk açılışta otomatik tarih/saat doldur
          meeting_date: currentDate,
          meeting_time: currentTime,
          meeting_consultant: defaultConsultantId,
          created_by: defaultConsultantId,
          follow_up_date: prev.follow_up_date || currentDate,
          registration_year: initialRegistrationYear || prev.registration_year || '',
          ...prefilledData,
          phone: prefilledPhone || prefilledData?.phone || prev.phone
        };

        // Email'leri kontrol et ve ekle
        if (defaultEmail || prefilledData?.email) {
          const newEmail = defaultEmail || prefilledData?.email;
          if (!baseData.emails.includes(newEmail)) {
            baseData.emails = [newEmail, ...baseData.emails.filter(e => e)];
          }
        }

        return baseData;
      });
    }
  }, [isOpen, user, defaultEmail, prefilledData, prefilledPhone, profiles, initialRegistrationYear]);

  // Skorları otomatik hesapla
  // --- Tıp akışı kısıtlarını uygula ---
  useEffect(() => {
    const isMedical = formData.medical_track === 'tip_ingilizce';

    if (isMedical) {
      setFormData(prev => {
        const nextPrograms = ['Tıp'];
        const nextUnis = (prev.interested_universities || []).filter(u => {
          const baseName = u.replace(/\s*\((Devlet|Özel)\)\s*$/, '');
          return MEDICAL_UNIVERSITIES.some(m => normalizeText(m.name) === normalizeText(baseName));
        });
        return {
          ...prev,
          interested_programs: nextPrograms,
          interested_universities: nextUnis
        };
      });
    }
  }, [formData.medical_track]);

  const normalizeText = (text: string) =>
    text.toLowerCase().trim().replace(/[^\w\sğüşöçıİĞÜŞÖÇ\-().]/g, '').replace(/\s+/g, ' ');

  const programExists = (program: string) =>
    [...availablePrograms, ...formData.interested_programs].some(p => normalizeText(p) === normalizeText(program));

  const universityExists = (university: string) => {
    const baseName = university.replace(/\s*\((Devlet|Özel)\)\s*$/, '');
    return [...availableUniversities, ...formData.interested_universities, ...MEDICAL_UNIVERSITIES.map(m => m.name)]
      .some(u => {
        const uBase = u.replace(/\s*\((Devlet|Özel)\)\s*$/, '');
        return normalizeText(uBase) === normalizeText(baseName);
      });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    // Phone restriction logic
    if (name === 'phone' || name === 'follow_up_phone' || name === 'student_info.phone') {
      let cleanVal = value.replace(/[^\d+]/g, ''); // Sadece rakam ve + işaretine izin ver

      // Specific state updates
      if (name === 'student_info.phone') {
        setFormData(prev => ({
          ...prev,
          student_info: {
            ...prev.student_info,
            phone: cleanVal
          }
        }));
        return;
      }
      setFormData(prev => ({ ...prev, [name]: cleanVal }));
      return;
    }

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      if (name === 'is_registered') {
        setFormData(prev => ({
          ...prev,
          [name]: checked,
          status: checked ? 'enrolled' : prev.status,
          tracking_status: checked ? 'enrolled' : 'new_lead'
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const addProgram = () => {
    const program = newProgram.trim();
    if (!program) return;
    setFormData(prev => ({ ...prev, interested_programs: [...prev.interested_programs, program] }));
    setNewProgram('');
  };

  const addUniversity = () => {
    const university = newUniversity.trim();
    if (!university) return;
    setFormData(prev => ({ ...prev, interested_universities: [...prev.interested_universities, university] }));
    setNewUniversity('');
  };

  const removeProgram = (program: string) =>
    setFormData(prev => ({ ...prev, interested_programs: prev.interested_programs.filter(p => p !== program) }));

  const removeUniversity = (university: string) =>
    setFormData(prev => ({
      ...prev,
      interested_universities: prev.interested_universities.filter(u => u !== university)
    }));

  const addEmail = (isStudent: boolean = true) => {
    if (isStudent) {
      setFormData(prev => ({ ...prev, emails: [...prev.emails, ''] }));
    } else {
      setFormData(prev => ({
        ...prev,
        student_info: { ...prev.student_info, emails: [...prev.student_info.emails, ''] }
      }));
    }
  };

  const removeEmail = (index: number, isStudent: boolean = true) => {
    if (isStudent) {
      setFormData(prev => ({ ...prev, emails: prev.emails.filter((_, i) => i !== index) }));
    } else {
      setFormData(prev => ({
        ...prev,
        student_info: { ...prev.student_info, emails: prev.student_info.emails.filter((_, i) => i !== index) }
      }));
    }
  };

  const handleEmailChange = (index: number, value: string, isStudent: boolean = true) => {
    if (isStudent) {
      setFormData(prev => {
        const newEmails = [...prev.emails];
        newEmails[index] = value;
        return { ...prev, emails: newEmails };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        student_info: {
          ...prev.student_info,
          emails: prev.student_info.emails.map((e, i) => i === index ? value : e)
        }
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      emails: [''],
      phone: '',
      nationality: 'Türkiye',
      has_italy_residence_permit: false,
      has_other_citizenship: false,
      other_citizenship_country: '',
      created_by: '',
      contact_role: 'student',
      student_info: {
        first_name: '',
        last_name: '',
        emails: [''],
        phone: '',
        nationality: 'Türkiye',
        education_level: '',
        english_level: '',
        italian_level: ''
      },
      guardian_info: {},
      interested_services: [],
      service_year: '2025',
      academic_year: '2025-2026',
      registration_year: '',
      medical_track: 'none',
      interested_programs: [],
      interested_universities: [],
      interested_countries: ['İtalya'],
      education_level: '',
      english_level: '',
      italian_level: '',
      work_experience: '',
      meeting_date: '',
      meeting_time: '',
      meeting_consultant: '',
      meeting_type: 'phone',
      meeting_summary: '',
      source: 'website',
      status: 'lead',
      priority: 'medium',
      is_serious_candidate: false,
      visa_info_provided: false,
      has_discount: false,
      discount_info: '',
      discussed_price: '',
      price_notes: '',
      additional_payment: '',
      contract_sent: false,
      form_completed: false,
      email_sent: false,
      is_registered: false,
      tracking_status: 'new_lead',
      reference_source: '',
      follow_up_required: false,
      follow_up_date: '',
      follow_up_notes: '',
      follow_up_name: '',
      follow_up_surname: '',
      follow_up_phone: '',
      follow_up_status: 'pending',
      follow_up_address: '',
      follow_up_time: '',
      notes: ''
    });



  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    // Validasyon kontrolleri
    const validationErrors: string[] = [];

    // Sadece ad soyad zorunlu
    if (!formData.first_name.trim()) {
      validationErrors.push('Ad Soyad alanı zorunludur');
    }

    // Eğer validasyon hatası varsa, uyarı göster ve işlemi durdur
    if (validationErrors.length > 0) {
      setError(validationErrors.join('\n'));
      setIsSaving(false);
      alert('Lütfen aşağıdaki zorunlu alanları doldurun:\n\n' + validationErrors.join('\n'));
      return;
    }

    try {
      // Telefon numarası kontrolü (Duplicate Check)
      if (formData.phone) {
        const { data: existingLead, error: checkError } = await supabase
          .from('leads')
          .select(`
            id,
            branch_id,
            branches (
              name
            )
          `)
          .eq('phone', formData.phone)
          .limit(1)
          .maybeSingle();

        if (checkError) {
          console.error('Lead kontrolü sırasında hata:', checkError);
          // Hata olsa bile devam edebiliriz veya durdurabiliriz.
          // Şimdilik devam etmemeyi seçersek: 
          // Ama kullanıcıyı engellememek adına loglayıp devam edelim.
        } else if (existingLead) {
          // İlişki tipine göre branches bir obje ya da dizi gelebilir.
          // Genellikle select(...) ile foreign key üzerinden join yapınca obje gelir ama güvenli olması için cast edelim.
          const branchInfo = existingLead.branches as any;
          const branchName = branchInfo?.name || (Array.isArray(branchInfo) ? branchInfo[0]?.name : 'Bilinmeyen');

          const confirmSave = await window.appConfirm(
            `Bu telefon numarası zaten ${branchName} şubesinde kayıtlı. Kaydetmek istediğinize emin misiniz?`
          );

          if (!confirmSave) {
            setIsSaving(false);
            return;
          }
        }
      }

      // Email'leri temizle (boş olanları çıkar)
      const cleanEmails = formData.emails.filter(e => e.trim());
      const cleanStudentEmails = formData.student_info.emails.filter(e => e.trim());

      const cleanedData = {
        ...formData,
        emails: cleanEmails,
        student_info: {
          ...formData.student_info,
          emails: cleanStudentEmails
        },
        meeting_date: formData.meeting_date || null,
        meeting_time: formData.meeting_time || null,
        notes: formData.notes || null,
        // Follow-up alanlarını temiz ve tutarlı gönder
        follow_up_date: formData.follow_up_required ? (formData.follow_up_date || null) : null,
        follow_up_notes: formData.follow_up_required ? (formData.follow_up_notes || null) : null,
        follow_up_name: formData.follow_up_required ? (formData.follow_up_name || null) : null,
        follow_up_surname: formData.follow_up_required ? (formData.follow_up_surname || null) : null,
        follow_up_phone: formData.follow_up_required ? (formData.follow_up_phone || null) : null,
        follow_up_status: formData.follow_up_required
          ? (formData.follow_up_date ? 'scheduled' : 'pending')
          : 'pending'
      };

      const consultantId = formData.meeting_consultant || (user as any)?.id;

      // Determine effective branch from the assigned consultant (creator)
      // This ensures that if an Admin creates a lead for an Izmir consultant, 
      // the lead gets the Izmir branch ID.
      const assignedProfile = profiles.find(p => p.id === consultantId);
      const effectiveBranchId = assignedProfile?.branch_id || (user as any)?.branchId;

      // Eski tarihli lead ise, created_at'i görüşme tarih ve saatine göre ayarla
      let createdAtDate: Date;
      if (isHistoricalLead && formData.meeting_date) {
        // meeting_date ve meeting_time'dan tam tarih oluştur
        const dateStr = formData.meeting_date;
        const timeStr = formData.meeting_time || '00:00';
        createdAtDate = new Date(`${dateStr}T${timeStr}:00`);
      } else {
        createdAtDate = new Date();
      }

      const leadData = {
        ...cleanedData,
        created_by: consultantId,
        branch_id: effectiveBranchId,
        assigned_consultants: [consultantId],
        created_at: createdAtDate.toISOString(),
        updated_at: createdAtDate.toISOString()
      };

      let result: any;
      if (onSave) {
        result = await onSave(leadData);
        if (!result.ok) {
          setError(result.error || 'Lead kaydedilirken hata oluştu');
          setIsSaving(false);
          return;
        }
      } else {
        const { data, error } = await supabase.from('leads').insert(leadData).select().single();
        if (error) {
          setError(error.message);
          setIsSaving(false);
          return;
        }
        result = { ok: true, data };
      }

      // Send welcome SMS if phone number exists
      console.log('SMS Gönderim Kontrolü:', {
        leadId: result.data?.id,
        phone: cleanedData.phone,
        willSend: !!(result.data?.id && cleanedData.phone)
      });

      if (result.data?.id && cleanedData.phone) {
        console.log('SMS gönderiliyor...', { branchId: effectiveBranchId });
        try {
          // Fetch the branch template if it exists to pass it directly
          let customSmsMessage = undefined;
          if (effectiveBranchId) {
            const { data: branchData } = await supabase
              .from('branches')
              .select('welcome_sms_template')
              .eq('id', effectiveBranchId)
              .maybeSingle();

            if (branchData?.welcome_sms_template) {
              customSmsMessage = branchData.welcome_sms_template;
            }
          }

          const { data: smsData, error: smsError } = await supabase.functions.invoke('send-welcome-sms', {
            body: {
              lead_id: result.data.id,
              first_name: cleanedData.first_name,
              last_name: cleanedData.last_name,
              phone: cleanedData.phone,
              branch_id: effectiveBranchId,
              custom_message: customSmsMessage
            }
          });

          if (smsError) {
            console.error('SMS gönderme hatası:', smsError);
          } else {
            console.log('SMS başarıyla gönderildi:', smsData);
          }
        } catch (smsErr) {
          console.error('SMS gönderme hatası:', smsErr);
        }
      } else {
        console.log('SMS gönderilmedi - koşullar sağlanmadı');
      }




      // Eğer status "enrolled" ise:
      // 1. Otomatik anlaşma kaydı oluştur (Mevcut)
      // 2. Future Registrations (Satış kayıtları) listesine ekle (YENİ)
      // Eğer status "enrolled" ise:
      // 1. Otomatik "Accepted Proposal" oluştur (Sales Raporları için)
      // 2. Future Registrations listesine ekle (Operasyon/Satış Takip Listesi)
      if (formData.status === 'enrolled') {
        try {
          // --- 1. Proposal Creation (For Sales Reports) ---
          const discussedPrice = formData.discussed_price || '0';
          const priceValue = parseFloat(discussedPrice.toString().replace(/[^0-9.-]/g, '')) || 0;
          const phoneNumber = formData.phone || formData.student_info?.phone || 'Belirtilmedi';

          // valid_until for proposal
          const validUntil = new Date();
          validUntil.setDate(validUntil.getDate() + 30);

          const proposalData = {
            lead_id: result.data?.id,
            student_name: `${formData.first_name} ${formData.last_name}`,
            student_email: cleanEmails[0] || 'Belirtilmedi',
            university: 'Kayıt Oldu',
            program: cleanedData.interested_programs?.[0] || 'Kayıt Oldu',
            degree: 'Kayıt Oldu',
            total_amount: priceValue,
            service_fee: priceValue,
            currency: 'EUR',
            status: 'accepted',
            created_by: consultantId,
            consultant_name: (user as any)?.name || 'Bilinmiyor',
            valid_until: validUntil.toISOString(),
            notes: `Öğrenci: ${formData.first_name} ${formData.last_name}\nTelefon: ${phoneNumber}\nEmail: ${cleanEmails[0] || 'Belirtilmedi'}\nFiyat: ${discussedPrice} EUR\n\n${formData.notes || ''}`,
            created_at: createdAtDate.toISOString(),
            updated_at: createdAtDate.toISOString()
          };

          const { error: proposalError } = await supabase.from('proposals').insert(proposalData);
          if (proposalError) console.error('Proposal auto-create error:', proposalError);


          // --- 2. Future Registrations Creation (For Checklist) ---
          const { error: regError } = await supabase.from('future_registrations').insert({
            registration_year: formData.registration_year || new Date().getFullYear().toString(),
            consultant_name: (user as any)?.name || 'Sistem',
            student_name: `${cleanedData.first_name} ${cleanedData.last_name}`,
            department: cleanedData.interested_programs?.[0] || '',
            branch_id: effectiveBranchId,
            created_at: new Date().toISOString()
          });

          if (regError) console.error('Future registration insert error:', regError);

          console.log('✅ Auto-enroll records created (Proposal + FutureRegistration)');

        } catch (err) {
          console.error('Auto-enroll error:', err);
        }
      }

      onSaved && onSaved(result);
      resetForm();
      onClose();
    } catch (err) {
      console.error('Lead kaydetme hatası:', err);
      setError('Beklenmeyen bir hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  // ====== Sekme İçerikleri ======

  const renderBasicInfo = () => (
    <div className="space-y-6">
      {/* Bölüm Başlığı */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <User className="w-5 h-5 text-blue-600" />
          Temel Bilgiler
        </h3>
      </div>

      {/* İletişim Kişisi Türü */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          İletişim Kişisi <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 gap-4">
          <label
            className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.contact_role === 'student'
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
          >
            <input
              type="radio"
              name="contact_role"
              value="student"
              checked={formData.contact_role === 'student'}
              onChange={handleChange}
              className="sr-only"
            />
            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Öğrenci</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Öğrenci kendisi arıyor</p>
            </div>
          </label>

          <label
            className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.contact_role === 'guardian'
              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
          >
            <input
              type="radio"
              name="contact_role"
              value="guardian"
              checked={formData.contact_role === 'guardian'}
              onChange={handleChange}
              className="sr-only"
            />
            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
              <Heart className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Veli</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Veli/Aile arıyor</p>
            </div>
          </label>
        </div>
      </div>

      {/* İletişim Kişisi Bilgileri */}
      <div>
        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
          {formData.contact_role === 'student' ? 'Öğrenci' : 'Veli'} İletişim Bilgileri
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {formData.contact_role === 'guardian' ? 'Veli Ad Soyadı' : 'Ad Soyad'} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
              placeholder={formData.contact_role === 'guardian' ? 'Veli Ad Soyadı' : 'Ad Soyad'}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Telefon</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+905423617248"
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email Adresleri</label>
            <div className="space-y-2">
              {formData.emails.map((email, index) => (
                <div key={index} className="flex space-x-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => handleEmailChange(index, e.target.value, true)}
                    placeholder="ornek@email.com"
                    className="flex-1 px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {formData.emails.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeEmail(index, true)}
                      className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addEmail(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Email Ekle</span>
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Uyruk</label>
            <input
              type="text"
              name="nationality"
              value={formData.nationality}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Veli ise öğrenci bilgileri */}
      {formData.contact_role === 'guardian' && (
        <div>
          <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Öğrenci Bilgileri</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Öğrenci Ad Soyad
              </label>
              <input
                type="text"
                name="student_info.first_name"
                value={formData.student_info.first_name}
                onChange={handleChange}
                placeholder="Öğrenci Ad Soyad"
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Öğrenci Telefonu</label>
              <input
                type="tel"
                name="student_info.phone"
                value={formData.student_info.phone}
                onChange={handleChange}
                placeholder="+905423617248"
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Öğrenci Email Adresleri</label>
              <div className="space-y-2">
                {formData.student_info.emails.map((email, index) => (
                  <div key={index} className="flex space-x-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => handleEmailChange(index, e.target.value, false)}
                      placeholder="ogrenci@email.com"
                      className="flex-1 px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {formData.student_info.emails.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEmail(index, false)}
                        className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addEmail(false)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>Email Ekle</span>
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Öğrenci Uyruğu</label>
              <input
                type="text"
                name="student_info.nationality"
                value={formData.student_info.nationality}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      )}

      {/* Vatandaşlık Bilgileri */}
      <div>
        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Vatandaşlık Bilgileri</h4>
        <div className="space-y-4">
          <div className="flex items-center">
            <input
              id="has_italy_residence_permit"
              type="checkbox"
              name="has_italy_residence_permit"
              checked={formData.has_italy_residence_permit}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="has_italy_residence_permit" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
              İtalya oturum izni var
            </label>
          </div>

          <div className="flex items-center">
            <input
              id="has_other_citizenship"
              type="checkbox"
              name="has_other_citizenship"
              checked={formData.has_other_citizenship}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="has_other_citizenship" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
              Başka ülke vatandaşlığı var
            </label>
          </div>

          {formData.has_other_citizenship && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Diğer Vatandaşlık Ülkesi</label>
              <input
                type="text"
                name="other_citizenship_country"
                value={formData.other_citizenship_country}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Örn: Almanya"
              />
            </div>
          )}
        </div>
      </div>

      {/* Lead Bilgileri */}
      <div>
        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Lead Bilgileri</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Durum</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="lead">Yeni Lead</option>
              <option value="rejected">Red</option>
              <option value="enrolled">Kayıt</option>
            </select>
          </div>

          <div className="">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Öncelik</label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="low">Düşük</option>
              <option value="medium">Orta</option>
              <option value="high">Yüksek</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kaynak</label>
            <select
              name="source"
              value={formData.source}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="website">Website</option>
              <option value="referral">Referans</option>
              <option value="social-media">Sosyal Medya</option>
              <option value="advertisement">Reklam</option>
              <option value="walk-in">Ziyaret</option>
              <option value="other">Diğer</option>
            </select>
          </div>

          <div className="">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Referans Kaynağı</label>
            <input
              type="text"
              name="reference_source"
              value={formData.reference_source}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Örn: Arkadaş tavsiyesi"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderEducationInfo = () => {
    const isMedical = formData.medical_track === 'tip_ingilizce';
    const isBoth = formData.medical_track === 'both';

    let universityOptions = availableUniversities;
    if (isMedical) {
      universityOptions = MEDICAL_UNIVERSITIES.map(u => `${u.name} (${u.type})`);
    } else if (isBoth) {
      universityOptions = [
        ...MEDICAL_UNIVERSITIES.map(u => `${u.name} (${u.type})`),
        ...availableUniversities
      ].sort((a, b) => a.localeCompare(b));
    }

    return (
      <div className="space-y-6">
        {/* Bölüm Başlığı */}
        <div className="border-b border-gray-200 dark:border-gray-700 pb-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-blue-600" />
            Eğitim & İlgi Alanları
          </h3>
        </div>

        {/* Eğitim Bilgileri */}
        <div>
          <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Eğitim Bilgileri</h4>

          {/* YENİ: Tıp (İngilizce) akışı */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Eğitim Türü</label>
              <select
                name="medical_track"
                value={formData.medical_track}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="none">Genel</option>
                <option value="tip_ingilizce">Tıp (İngilizce)</option>
                <option value="both">Genel + Tıp</option>
              </select>
            </div>

            <div className="hidden md:block" />
          </div>

          {isMedical && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 mb-2">
              <div className="flex items-start space-x-3">
                <Info className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5" />
                <div className="text-sm text-emerald-900 dark:text-emerald-300">
                  Tıp (İngilizce) akışı seçildi. <strong>Program</strong> otomatik <strong>Tıp</strong> yapılır ve düzenlenemez.
                  Üniversite listesinde her okulun yanında (Devlet) veya (Özel) bilgisi gösterilir.
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Eğitim Seviyesi</label>
              <select
                name={formData.contact_role === 'student' ? 'education_level' : 'student_info.education_level'}
                value={formData.contact_role === 'student' ? formData.education_level : formData.student_info.education_level}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Seçin</option>
                <option value="Lise">Lise</option>
                <option value="Ön Lisans">Ön Lisans</option>
                <option value="Lisans">Lisans</option>
                <option value="Yüksek Lisans">Yüksek Lisans</option>
                <option value="Doktora">Doktora</option>
                <option value="Mezun">Mezun</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">İngilizce Seviyesi</label>
              <select
                name={formData.contact_role === 'student' ? 'english_level' : 'student_info.english_level'}
                value={formData.contact_role === 'student' ? formData.english_level : formData.student_info.english_level}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Bilmiyor</option>
                <option value="A1">A1 - Başlangıç</option>
                <option value="A2">A2 - Temel</option>
                <option value="B1">B1 - Orta Alt</option>
                <option value="B2">B2 - Orta Üst</option>
                <option value="C1">C1 - İleri</option>
                <option value="C2">C2 - Üst Düzey</option>
                <option value="Bilmiyor">Bilmiyor</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">İtalyanca Seviyesi</label>
              <select
                name={formData.contact_role === 'student' ? 'italian_level' : 'student_info.italian_level'}
                value={formData.contact_role === 'student' ? formData.italian_level : formData.student_info.italian_level}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Bilmiyor</option>
                <option value="A1">A1 - Başlangıç</option>
                <option value="A2">A2 - Temel</option>
                <option value="B1">B1 - Orta Alt</option>
                <option value="B2">B2 - Orta Üst</option>
                <option value="C1">C1 - İleri</option>
                <option value="C2">C2 - Üst Düzey</option>
                <option value="Bilmiyor">Bilmiyor</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Akademik Yıl</label>
              <select
                name="academic_year"
                value={formData.academic_year}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Seçin</option>
                {academicYears.map(y => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kayıt Yılı (Gelecek Yıl Kayıtları)</label>
              <select
                name="registration_year"
                value={formData.registration_year}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Seçin (Opsiyonel)</option>
                <option value="2026">2026</option>
                <option value="2027">2027</option>
                <option value="2028">2028</option>
                <option value="2029">2029</option>
                <option value="2030">2030</option>
              </select>
            </div>
          </div>
        </div>

        {/* İlgi Alanları */}
        <div>
          <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">İlgi Alanları</h4>

          {/* Hizmet */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">İlgilenilen Hizmetler</label>

            <div className="flex flex-wrap gap-2 mb-3">
              {formData.interested_services.map(service => (
                <span
                  key={service}
                  className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 dark:bg-blue-600/20 dark:text-blue-300 text-sm rounded-full border border-blue-200 dark:border-blue-600/30"
                >
                  <span>{service}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        interested_services: prev.interested_services.filter(s => s !== service)
                      }));
                    }}
                    className="ml-1 text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-100"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>

            <select
              onChange={(e) => {
                const service = e.target.value;
                if (service && !formData.interested_services.includes(service)) {
                  setFormData(prev => ({
                    ...prev,
                    interested_services: [...prev.interested_services, service]
                  }));
                }
                e.target.value = '';
              }}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Hizmet Seçin</option>
              <option value="Ücretsiz Danışmanlık">Ücretsiz Danışmanlık</option>
              <option value="Danışmanlık">Danışmanlık</option>
              <option value="Meslek Danışmanlığı">Meslek Danışmanlığı</option>
              <option value="Vize">Vize</option>
              <option value="Konaklama">Konaklama</option>
              <option value="Burs">Burs</option>
              <option value="Yaşam Destek">Yaşam Destek</option>
              <option value="Sınav Kayıt">Sınav Kayıt</option>
              <option value="Transfer">Transfer</option>
              <option value="Eğitim">Eğitim</option>
              <option value="Vasi">Vasi</option>
              <option value="Kabul Sonrası">Kabul Sonrası</option>
            </select>
          </div>

          {/* Programlar */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">İlgilenilen Programlar</label>

            {isMedical ? (
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center space-x-1 px-3 py-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-600/20 dark:text-emerald-300 text-sm rounded-full border border-emerald-200 dark:border-emerald-600/30">
                  <CheckCircle className="w-3 h-3" />
                  <span>Tıp</span>
                </span>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.interested_programs.map(program => (
                    <span
                      key={program}
                      className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 dark:bg-green-600/20 dark:text-green-300 text-sm rounded-full border border-green-200 dark:border-green-600/30"
                    >
                      <span>{program}</span>
                      <button
                        type="button"
                        onClick={() => removeProgram(program)}
                        className="hover:text-green-600 dark:hover:text-green-200"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>

                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newProgram}
                    onChange={e => setNewProgram(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addProgram();
                      }
                    }}
                    placeholder="Program adı ekle..."
                    className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={addProgram}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center space-x-1"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Ekle</span>
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Üniversiteler */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">İlgilenilen Üniversiteler</label>

            <div className="flex flex-wrap gap-2 mb-3">
              {formData.interested_universities.map(university => (
                <span
                  key={university}
                  className="flex items-center space-x-1 px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-600/20 dark:text-purple-300 text-sm rounded-full border border-purple-200 dark:border-purple-600/30"
                >
                  <span>{university}</span>
                  <button
                    type="button"
                    onClick={() => removeUniversity(university)}
                    className="hover:text-purple-600 dark:hover:text-purple-200"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>

            <div className="relative">
              <div className="flex space-x-2">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={newUniversity}
                    onChange={e => {
                      const query = e.target.value;
                      setNewUniversity(query);
                      if (query.trim().length > 0) {
                        const filtered = universityOptions.filter(u =>
                          normalizeText(u).includes(normalizeText(query)) &&
                          !formData.interested_universities.some(sel => normalizeText(sel) === normalizeText(u))
                        );
                        setFilteredUniversities(filtered);
                      } else {
                        setFilteredUniversities([]);
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addUniversity();
                      }
                    }}
                    placeholder="Üniversite adı ekle (yazarken ara)..."
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {filteredUniversities.length > 0 && newUniversity && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredUniversities.map(uni => (
                        <button
                          key={uni}
                          type="button"
                          onClick={() => {
                            if (!formData.interested_universities.includes(uni)) {
                              setFormData(prev => ({
                                ...prev,
                                interested_universities: [...prev.interested_universities, uni]
                              }));
                            }
                            setNewUniversity('');
                            setFilteredUniversities([]);
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          {uni}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={addUniversity}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center space-x-1"
                >
                  <Plus className="w-4 h-4" />
                  <span>Ekle</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMeetingInfo = () => (
    <div className="space-y-6">
      {/* Bölüm Başlığı */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          Görüşme & Mali Bilgiler
        </h3>
      </div>

      {/* Görüşme Bilgileri */}
      <div>
        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Görüşme Bilgileri</h4>

        {!isHistoricalLead && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-3">
              <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <h5 className="font-medium text-blue-900 dark:text-blue-300">Otomatik Dolduruldu</h5>
                <p className="text-sm text-blue-800 dark:text-blue-400 mt-1">
                  Görüşme tarihi ve saati otomatik olarak doldurulmuştur.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Görüşme Tarihi</label>
            <input
              type="date"
              name="meeting_date"
              value={formData.meeting_date}
              onChange={handleChange}
              readOnly={!isHistoricalLead}
              className={`w-full px-4 py-3 border rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isHistoricalLead
                ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                : 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Görüşme Saati</label>
            <input
              type="time"
              name="meeting_time"
              value={formData.meeting_time}
              onChange={handleChange}
              readOnly={!isHistoricalLead}
              className={`w-full px-4 py-3 border rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isHistoricalLead
                ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                : 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Danışman <span className="text-red-500">*</span>
            </label>
            <select
              name="meeting_consultant"
              value={formData.meeting_consultant}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Danışman Seçin</option>
              {profiles
                .filter(p => {
                  // Sales unit_type'ında olan kullanıcıları filtrele
                  if (p.unit_type !== 'sales') return false;

                  // Eğer giriş yapan kullanıcının branch_id'si varsa, aynı şubedeki kullanıcıları göster
                  const currentUserProfile = profiles.find(prof => prof.id === (user as any)?.id);
                  if (currentUserProfile?.branch_id && p.branch_id) {
                    return p.branch_id === currentUserProfile.branch_id;
                  }

                  // Branch_id yoksa tüm sales kullanıcılarını göster
                  return true;
                })
                .map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text sm font-medium text-gray-700 dark:text-gray-300 mb-2">Görüşme Türü</label>
            <select
              name="meeting_type"
              value={formData.meeting_type}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="email">E-mail</option>
              <option value="phone">Telefonda Görüşme</option>
              <option value="whatsapp">WhatsApp Mesajlaşma</option>
              <option value="video">Online Görüşme</option>
              <option value="in-person">Fiziki Görüşme</option>
            </select>
          </div>
        </div>
      </div>

      {/* Mali Bilgiler */}
      <div>
        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Mali Bilgiler</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Konuşulan Ücret (EUR)</label>
            <input
              type="number"
              name="discussed_price"
              value={formData.discussed_price}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Sadece rakam giriniz (Örn: 15000)"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ek Ödeme (EUR)</label>
            <input
              type="number"
              name="additional_payment"
              value={formData.additional_payment}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Sadece rakam giriniz (Örn: 1000)"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">İndirim Bilgileri</label>
            <input
              type="text"
              name="discount_info"
              value={formData.discount_info}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Örn: Erken kayıt %10 indirim"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ücret Ek Bilgi</label>
            <textarea
              name="price_notes"
              value={formData.price_notes}
              onChange={handleChange}
              rows={2}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Taksit detayları, ödeme planı, ek bilgiler..."
            />
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center">
            <input
              id="has_discount"
              type="checkbox"
              name="has_discount"
              checked={formData.has_discount}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="has_discount" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
              İndirim Uygulandı
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStatusTracking = () => (
    <div className="space-y-6">
      {/* Bölüm Başlığı */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Target className="w-5 h-5 text-blue-600" />
          Durum Takibi
        </h3>
      </div>

      {/* Durum Takibi */}
      <div>
        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Lead Durumu</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-center">
            <input
              id="is_serious_candidate"
              type="checkbox"
              name="is_serious_candidate"
              checked={formData.is_serious_candidate}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="is_serious_candidate" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
              Ciddi Aday
            </label>
          </div>
          <div className="flex items-center">
            <input
              id="visa_info_provided"
              type="checkbox"
              name="visa_info_provided"
              checked={formData.visa_info_provided}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="visa_info_provided" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
              Vize Bilgisi Verildi
            </label>
          </div>
          <div className="flex items-center">
            <input
              id="contract_sent"
              type="checkbox"
              name="contract_sent"
              checked={formData.contract_sent}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="contract_sent" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
              Sözleşme Gönderildi
            </label>
          </div>
          <div className="flex items-center">
            <input
              id="form_completed"
              type="checkbox"
              name="form_completed"
              checked={formData.form_completed}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="form_completed" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
              Form Tamamlandı
            </label>
          </div>
          <div className="flex items-center">
            <input
              id="email_sent"
              type="checkbox"
              name="email_sent"
              checked={formData.email_sent}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="email_sent" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
              Email Gönderildi
            </label>
          </div>
          <div className="flex items-center">
            <input
              id="is_registered"
              type="checkbox"
              name="is_registered"
              checked={formData.is_registered}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="is_registered" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
              Kayıt Oldu
            </label>
          </div>
        </div>
      </div>

      {/* Görüşme Özeti */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Görüşme Özeti</label>
        <textarea
          name="meeting_summary"
          value={formData.meeting_summary}
          onChange={handleChange}
          rows={4}
          className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          placeholder="Görüşme detayları ve önemli notlar..."
        />
      </div>

      {/* Follow-up Planlama */}
      <div className="mt-2">
        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">Follow-up</h4>
        <div className="p-4 rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 space-y-4">
          <div className="flex items-center">
            <input
              id="follow_up_required"
              type="checkbox"
              name="follow_up_required"
              checked={formData.follow_up_required}
              onChange={handleChange}
              className="w-4 h-4 text-yellow-600 bg-gray-100 border-gray-300 rounded focus:ring-yellow-500"
            />
            <label htmlFor="follow_up_required" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
              Follow-up planla
            </label>
          </div>

          {formData.follow_up_required && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">İsim</label>
                  <input
                    type="text"
                    name="follow_up_name"
                    value={formData.follow_up_name}
                    onChange={handleChange}
                    placeholder="İsim"
                    className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Soyisim</label>
                  <input
                    type="text"
                    name="follow_up_surname"
                    value={formData.follow_up_surname}
                    onChange={handleChange}
                    placeholder="Soyisim"
                    className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Telefon</label>
                  <input
                    type="tel"
                    name="follow_up_phone"
                    value={formData.follow_up_phone}
                    onChange={handleChange}
                    placeholder="+905423617248"
                    className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tarih</label>
                  <input
                    type="date"
                    name="follow_up_date"
                    value={formData.follow_up_date}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Follow-up Notu</label>
                <textarea
                  name="follow_up_notes"
                  value={formData.follow_up_notes}
                  onChange={handleChange}
                  placeholder="Follow-up ile ilgili notlar..."
                  rows={3}
                  className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div >
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-5xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <User className="w-5 h-5 text-gray-700 dark:text-gray-200" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Yeni Lead Ekle</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300"
            aria-label="Kapat"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 pb-6 pt-6 max-h-[75vh] overflow-y-auto space-y-8">
            {error && (
              <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            )}

            {/* Eski Tarihli Lead Seçeneği - En Üstte */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-xl p-4">
              <div className="flex items-center space-x-3">
                <input
                  id="isHistoricalLead"
                  type="checkbox"
                  checked={isHistoricalLead}
                  onChange={(e) => {
                    setIsHistoricalLead(e.target.checked);
                    // Checkbox açıldığında tarih ve saati temizle
                    if (e.target.checked) {
                      setFormData(prev => ({
                        ...prev,
                        meeting_date: '',
                        meeting_time: ''
                      }));
                    } else {
                      // Checkbox kapatıldığında otomatik tarih/saat doldur
                      const now = new Date();
                      const currentDate = now.toISOString().split('T')[0];
                      const currentTime = now.toTimeString().slice(0, 5);
                      setFormData(prev => ({
                        ...prev,
                        meeting_date: currentDate,
                        meeting_time: currentTime
                      }));
                    }
                  }}
                  className="w-5 h-5 text-amber-600 bg-white border-gray-300 rounded focus:ring-amber-500 focus:ring-2"
                />
                <label htmlFor="isHistoricalLead" className="flex items-center gap-2 text-base font-semibold text-amber-900 dark:text-amber-200 cursor-pointer">
                  <Clock className="w-5 h-5" />
                  Eski tarihli lead (Görüşme tarih/saatini manuel gir)
                </label>
              </div>
              {isHistoricalLead && (
                <p className="text-sm text-amber-800 dark:text-amber-300 mt-2 ml-8">
                  Bu seçenek aktifken görüşme tarihi ve saatini kendiniz belirleyebilirsiniz.
                </p>
              )}
            </div>

            {renderBasicInfo()}
            {renderEducationInfo()}
            {renderMeetingInfo()}
            {renderStatusTracking()}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              * Follow-up aktifse, lead listesinde “Follow-up: Evet” olarak görünür ve Follow-up sayfasına düşer.
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 disabled:opacity-60"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddLeadModal;
